import {
  createStackHeaderNavigator,
  ModalCloseButton,
} from "@towersystems/roam-common-native";
import {
  ManageTowerPaymentsRootScreenParamList,
  SettingsRootParamList,
} from "../../../../types";
import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { ManageTowerPaymentsLandingScreen } from "./ManageTowerPaymentsLandingScreen";
import {
  PairedTowerPaymentsDevice,
  PaymentMethod,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useLazyQuery } from "@apollo/client";
import { QUERY_PAYMENT_METHOD } from "../../common";
import { ScreenContext } from "./context";
import { PairTowerPaymentsDeviceScreen } from "./PairTowerPaymentsDeviceScreen";
import {
  QUERY_PAIRED_TOWER_PAYMENTS_DEVICE,
  QUERY_TOWER_PAYMENTS_VALID_CONNECTION,
} from "../../../../common/queries";

const Stack =
  createStackHeaderNavigator<ManageTowerPaymentsRootScreenParamList>();

export interface ManageTowerPaymentsRootScreenProps
  extends StackScreenProps<
    SettingsRootParamList,
    "ManageTowerPaymentsRootScreen"
  > {}

export const ManageTowerPaymentsRootScreen = ({
  route,
  navigation,
}: ManageTowerPaymentsRootScreenProps) => {
  const [paymentMethod, setPaymentMethod] = React.useState<
    PaymentMethod | undefined
  >(undefined);

  const [pairedDevice, setPairedDevice] = React.useState<
    PairedTowerPaymentsDevice | undefined
  >(undefined);

  const [isValidConnection, setIsValidConnection] = React.useState<
    boolean | undefined
  >(undefined);

  const [queryPaymentMethod] = useLazyQuery<{
    paymentMethod: PaymentMethod;
  }>(QUERY_PAYMENT_METHOD);

  const [queryPaymentDevice] = useLazyQuery<{
    pairedTowerPaymentsDeviceByMethodId?: PairedTowerPaymentsDevice;
  }>(QUERY_PAIRED_TOWER_PAYMENTS_DEVICE);

  const [queryTowerPaymentsValidConnection] = useLazyQuery<{
    towerPaymentsValidConnection: { success: boolean };
  }>(QUERY_TOWER_PAYMENTS_VALID_CONNECTION);

  const paymentMethodId = route.params.paymentMethodId;

  const loadData = React.useCallback(
    (reset?: boolean) => {
      if (!paymentMethodId) {
        return Promise.resolve();
      }
      if (reset) {
        setPaymentMethod(undefined);
        setPairedDevice(undefined);
      }
      return Promise.all([
        queryPaymentMethod({ variables: { id: paymentMethodId } }),
        queryPaymentDevice({ variables: { paymentMethodId } }),
        queryTowerPaymentsValidConnection({ variables: { paymentMethodId } }),
      ]).then(
        ([
          paymentMethodResult,
          paymentDeviceResult,
          towerPaymentsValidConnectionResult,
        ]) => {
          if (paymentMethodResult.data?.paymentMethod) {
            setPaymentMethod(paymentMethodResult.data.paymentMethod);
          } else {
            throw new Error("Payment method not found");
          }
          if (paymentDeviceResult.data?.pairedTowerPaymentsDeviceByMethodId) {
            setPairedDevice(
              paymentDeviceResult.data.pairedTowerPaymentsDeviceByMethodId
            );
          } else {
            setPairedDevice(undefined);
          }
          if (
            towerPaymentsValidConnectionResult.data
              ?.towerPaymentsValidConnection
          ) {
            setIsValidConnection(
              towerPaymentsValidConnectionResult.data
                .towerPaymentsValidConnection.success
            );
          } else {
            setIsValidConnection(false);
          }
        }
      );
    },
    [paymentMethodId]
  );

  return (
    <ScreenContext.Provider
      value={{
        paymentMethod,
        pairedDevice,
        isValidConnection,
        refresh: loadData,
      }}
    >
      <Stack.Navigator
        screenOptions={{
          animationEnabled: true,
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
        }}
      >
        <Stack.Group
          screenOptions={{
            title: "",
            headerLeft: () => <ModalCloseButton type="Back" />,
          }}
        >
          <Stack.Screen
            name="ManageTowerPaymentsLandingScreen"
            component={ManageTowerPaymentsLandingScreen}
          />
          <Stack.Screen
            name="PairTowerPaymentsDeviceScreen"
            component={PairTowerPaymentsDeviceScreen}
            options={{
              headerLeft: () => <ModalCloseButton type="Cancel" />,
            }}
          />
        </Stack.Group>
      </Stack.Navigator>
    </ScreenContext.Provider>
  );
};
